import { z } from 'zod';
import { StorageObjectKeySchema } from './storage.js';

/**
 * @aiPurpose constants for the boxes-with-nested-items workflow
 *   (#599). Mirrors `RequestItemPackage*` bounds so the wire surface,
 *   backend Decimal arithmetic, web preview, and XLSX export all
 *   reference one set of literals. Decision #14 / #20 / #25 of issue
 *   #599: dimensions in integer cm with the same per-axis bound as
 *   packages, count cap mirroring the existing item count cap, weight
 *   and volume sharing the same `Decimal(13,4)` precision/overflow
 *   discipline.
 * @aiInvariant the per-axis cm bound matches
 *   `REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM` so a tampered payload cannot
 *   overflow downstream `Decimal(13,4)` precision when the box is
 *   multiplied by `count` and joined with item totals on a request.
 *   Per-request and per-box list bounds (500 / 500) close the
 *   pathological-payload class — without them a single request body
 *   could declare millions of nested rows and exhaust the editor and
 *   the persistence transaction.
 * @aiSecurity these bounds are a finiteness guarantee, not an authz
 *   control. Authority/visibility on box writes flows through the
 *   existing draft-save / request visibility gates (issue #599
 *   Decision #32 — no new RBAC permission).
 */
export const REQUEST_BOX_DIMENSION_MAX_CM = 100000;
export const REQUEST_BOX_COUNT_MAX = 999999;
export const REQUEST_BOX_WEIGHT_KG_MAX = 999_999_999.9999;
export const REQUEST_BOX_VOLUME_M3_MAX = 999_999_999.9999;
export const REQUEST_BOX_VOLUME_PRECISION_M3 = 0.0001;
export const REQUEST_BOXES_PER_REQUEST_MAX = 500;
export const REQUEST_BOX_ITEMS_PER_BOX_MAX = 500;
/**
 * @aiPurpose upper bound for `RequestBoxItem.groupOrdinal` (#704). The
 *   ordinal is the visual-grouping token assigned by the China-side
 *   logist inside one box; `0` is the default single-group case and
 *   gaps are allowed (e.g., `[0, 2]`). A finite ceiling closes the
 *   pathological-payload class — without it a single tampered body
 *   could declare an unbounded ordinal that the manager UX would have
 *   to render as an open-ended group list.
 * @aiInvariant the value also feeds the `CarrierBidScope.BOX_GROUP`
 *   `scopeRefId` token (`<boxId>:<groupOrdinal>`, sub-issue #6 of the
 *   parent epic) — keep it integer and non-negative so the token is
 *   unambiguous and URL-safe without extra encoding.
 */
export const REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX = 100;

/**
 * @aiPurpose discriminator for how a box's per-unit volume was sourced.
 *   `'direct'` — the manager typed `volumeM3` directly. `'dimensions'`
 *   — the server computed `volumeM3` from `widthCm * depthCm * heightCm
 *   / 1_000_000` (HALF_UP to 0.0001 m³). The retain-on-toggle UX keeps
 *   the inactive mode's inputs persisted across toggles (Decision #14
 *   of #599) so the discriminator is the only field that shifts which
 *   stored values are authoritative.
 * @aiInvariant a separate enum from item `dimensionsMode` (boolean,
 *   different semantics — Decision #12 of #599). Do NOT reuse the item
 *   discriminator; collapsing them would conflate two different state
 *   machines.
 */
export const RequestBoxVolumeModeSchema = z.enum(['direct', 'dimensions']);
export type RequestBoxVolumeMode = z.infer<typeof RequestBoxVolumeModeSchema>;

/**
 * @aiPurpose wire projection of one persisted nested item inside a
 *   `RequestBox`. Mirrors `RequestItem` field semantics for
 *   name/brand/description/photos so the manager UX feels symmetric
 *   (Decision #25 of #599: bounds mirror existing `RequestItem`).
 *   `quantity` is nullable because nested-item quantity is optional
 *   per Decision #24 and does not affect request weight/volume.
 * @aiInvariant `id` is a server-minted UUID; `sortOrder` is the
 *   deterministic UI/export order. `photos.max(1)` is the box-item cap;
 *   it stays at 1 by epic #1449 decision D7 and NO LONGER tracks the
 *   ordinary `RequestItem.photos` cap (now REQUEST_ITEM_PHOTOS_MAX).
 *   The storage scope is
 *   reused (`request-item-photo`, Decision § Storage of #599) so a
 *   nested-item photo flows through the same upload/cleanup contract
 *   as ordinary item photos. `quantity` lower bound is `min(0)` —
 *   identical to `DraftRequestItemSchema.quantity` for ordinary
 *   items. A persisted DRAFT may carry `0` for partial-typing
 *   tolerance; the SEND-readiness gate in `transitions.service` is
 *   the chokepoint that catches `present-but-not-positive`.
 */
export const RequestBoxItemSchema = z
  .object({
    id: z.string().uuid(),
    sortOrder: z.number().int().nonnegative(),
    /**
     * @aiPurpose visual grouping token (#704) — items with the same
     *   `groupOrdinal` inside one box ship together. Default `0`
     *   keeps the pre-#704 single-group case. Gaps are allowed
     *   (e.g., `[0, 2]`) so the logist can delete a middle group
     *   without renumbering siblings.
     */
    groupOrdinal: z.number().int().min(0).max(REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX),
    name: z.string(),
    brand: z.string(),
    material: z.string().optional(),
    description: z.string(),
    quantity: z.number().int().min(0).max(REQUEST_BOX_COUNT_MAX).nullable(),
    photos: z.array(StorageObjectKeySchema).max(1),
  })
  .strict();
export type RequestBoxItem = z.infer<typeof RequestBoxItemSchema>;

/**
 * @aiPurpose wire projection of one persisted box on a request.
 *   `weightTotalKg` and `volumeTotalM3` are server-authoritative
 *   contributions to the request aggregate (= per-unit value × count,
 *   `Decimal(13,4)` HALF_UP). Decision #17 of #599: the web UI
 *   displays these server-computed totals after save/refetch instead
 *   of re-multiplying with JS Number (drift-prone at high counts).
 * @aiInvariant `id` is a server-minted UUID (Decision #19 — no
 *   sequence-backed `bx-NNNN`). `widthCm`/`depthCm`/`heightCm` are
 *   nullable independently of `volumeMode` so retain-on-toggle keeps
 *   inactive-mode inputs (Decision #14). When `volumeMode ===
 *   'dimensions'`, the server recomputes `volumeM3` from the
 *   dimensions; when `'direct'`, `volumeM3` reflects the typed input.
 *   `weightTotalKg`/`volumeTotalM3` always equal `weightKg * count`
 *   and `volumeM3 * count` after the most recent save.
 */
export const RequestBoxSchema = z
  .object({
    id: z.string().uuid(),
    sortOrder: z.number().int().nonnegative(),
    count: z.number().int().nonnegative().max(REQUEST_BOX_COUNT_MAX),
    weightKg: z.number().nonnegative().max(REQUEST_BOX_WEIGHT_KG_MAX),
    volumeM3: z.number().nonnegative().max(REQUEST_BOX_VOLUME_M3_MAX),
    weightTotalKg: z.number().nonnegative().max(REQUEST_BOX_WEIGHT_KG_MAX),
    volumeTotalM3: z.number().nonnegative().max(REQUEST_BOX_VOLUME_M3_MAX),
    volumeMode: RequestBoxVolumeModeSchema,
    widthCm: z.number().int().nonnegative().max(REQUEST_BOX_DIMENSION_MAX_CM).nullable(),
    depthCm: z.number().int().nonnegative().max(REQUEST_BOX_DIMENSION_MAX_CM).nullable(),
    heightCm: z.number().int().nonnegative().max(REQUEST_BOX_DIMENSION_MAX_CM).nullable(),
    items: z.array(RequestBoxItemSchema),
    /**
     * @aiPurpose optimistic-concurrency token for the narrow regroup endpoint
     *   (#1178, D7). Reflects `RequestBox.updatedAt`, manually bumped inside
     *   the regroup transaction (Prisma does not propagate child-row updates
     *   to the parent box). The logist's frontend echoes the value back in
     *   the `If-Match` header (raw ISO string — NOT quoted ETag form); the
     *   server uses an atomic conditional update to detect drift
     *   (`P2025 → HTTP 412 DOMAIN_CONFLICT / box.version.stale`).
     *
     *   OPTIONAL on the wire by design. Emitted ONLY when the caller sets
     *   the opt-in `?includeBoxVersions=true` query flag (matches the
     *   stale-bundle-safe pattern from #590 / #599). Pre-#1178 SPA bundles
     *   never set the flag, never receive this field, never render the new
     *   "Edit groups" toggle (D17 read-only fallback) — so they cannot
     *   construct a stale `If-Match` from absent state. Follow-up after one
     *   deploy cycle drains stale bundles: make this required and drop the
     *   flag.
     * @aiInvariant strictly newer ISO after any regroup commit. Reading a
     *   box immediately after a successful regroup returns a value strictly
     *   greater than the `If-Match` the caller sent.
     */
    updatedAt: z.string().datetime().optional(),
  })
  .strict();
export type RequestBox = z.infer<typeof RequestBoxSchema>;

/**
 * @aiPurpose nested-item input for the draft-save aggregate path.
 *   `clientBoxItemId` is the client-owned local identity that the
 *   server maps back to the minted UUID via `boxItemIdMap`. `id` is
 *   present iff this nested item already exists on the server.
 * @aiInvariant cross-box / cross-request nested-item ids are rejected
 *   by the request-boxes service before any write, mirroring the
 *   existing item ownership behavior (issue #599 invisible/not-found
 *   pattern). Numeric/length bounds permit partially typed DRAFT/RETURN
 *   rows; SEND-readiness in `transitions.service` enforces the strict
 *   `quantity >= 1 || null` bound. `quantity` lower bound here is
 *   `min(0)` — same partial-typing tolerance as
 *   `DraftRequestItemSchema.quantity` for ordinary items. Persisted
 *   rows surface as `RequestBoxItemSchema` (`min(1)` strict).
 */
export const DraftSaveBoxItemInputSchema = z
  .object({
    clientBoxItemId: z.string().min(1).max(64),
    id: z.string().uuid().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
    /**
     * @aiPurpose visual grouping token (#704). Optional on the input so
     *   pre-#704 clients can keep posting nested items without sending
     *   a group; the service defaults missing values to `0` (single-
     *   group baseline). Bounded `0..REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX`
     *   to mirror the response schema and close the payload-size class.
     */
    groupOrdinal: z.number().int().min(0).max(REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX).optional(),
    name: z.string(),
    brand: z.string(),
    material: z.string().optional(),
    description: z.string(),
    quantity: z.number().int().min(0).max(REQUEST_BOX_COUNT_MAX).nullable().optional(),
    photos: z.array(StorageObjectKeySchema).max(1),
  })
  .strict();
export type DraftSaveBoxItemInput = z.infer<typeof DraftSaveBoxItemInputSchema>;

/**
 * @aiPurpose box input for the draft-save aggregate path. Each box
 *   carries its nested items array as authoritative for that box's
 *   children — adding/removing nested items happens through the same
 *   draft-save round trip, not through any separate sub-resource
 *   endpoint. Decision #30 of #599: boxes are only mutable in
 *   DRAFT/RETURN; the draft service rejects mutations for other
 *   statuses with `INVALID_TRANSITION` mirroring the item path.
 * @aiInvariant `volumeMode` is the discriminator that drives which
 *   stored value (`volumeM3` direct OR `widthCm*depthCm*heightCm`
 *   computed) the server treats as authoritative on this save. Both
 *   sets are persisted regardless to support retain-on-toggle UX.
 *   `items` is required and authoritative for this box's nested rows
 *   (full reconcile by `id` — omitted nested rows are deleted). The
 *   tristate ownership semantics (omit / `[]` / populated) operate
 *   one level up, on the top-level `boxes` field of
 *   `DraftSaveInputSchema`, not on this box-level `items`.
 */
export const DraftSaveBoxInputSchema = z
  .object({
    clientBoxId: z.string().min(1).max(64),
    id: z.string().uuid().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
    count: z.number().int().nonnegative().max(REQUEST_BOX_COUNT_MAX),
    weightKg: z.number().nonnegative().max(REQUEST_BOX_WEIGHT_KG_MAX),
    volumeM3: z.number().nonnegative().max(REQUEST_BOX_VOLUME_M3_MAX),
    volumeMode: RequestBoxVolumeModeSchema,
    widthCm: z.number().int().nonnegative().max(REQUEST_BOX_DIMENSION_MAX_CM).nullable().optional(),
    depthCm: z.number().int().nonnegative().max(REQUEST_BOX_DIMENSION_MAX_CM).nullable().optional(),
    heightCm: z
      .number()
      .int()
      .nonnegative()
      .max(REQUEST_BOX_DIMENSION_MAX_CM)
      .nullable()
      .optional(),
    items: z.array(DraftSaveBoxItemInputSchema).max(REQUEST_BOX_ITEMS_PER_BOX_MAX),
  })
  .strict();
export type DraftSaveBoxInput = z.infer<typeof DraftSaveBoxInputSchema>;

/**
 * @aiPurpose canonical-id map for the boxes the client sent alongside
 *   the draft-save body. The server emits one entry per box regardless
 *   of whether it was new or pre-existing so the client can re-baseline
 *   dirty state by mapped id.
 * @aiInvariant `clientBoxId` references a box the client also sent in
 *   the same body; `id` is the server UUID for the persisted box row.
 */
export const DraftSaveBoxIdMapEntrySchema = z
  .object({
    clientBoxId: z.string().min(1),
    id: z.string().uuid(),
  })
  .strict();
export type DraftSaveBoxIdMapEntry = z.infer<typeof DraftSaveBoxIdMapEntrySchema>;

/**
 * @aiPurpose canonical-id map for the nested items the client sent
 *   inside boxes on the draft-save body. The server emits one entry
 *   per nested-item row (new and pre-existing) so the client can
 *   re-baseline dirty state by mapped id at both layers — outer box
 *   plus inner item.
 * @aiInvariant `clientBoxId` references a box in the same body;
 *   `clientBoxItemId` is unique within that box; `id` is the server
 *   UUID for the persisted nested-item row.
 */
export const DraftSaveBoxItemIdMapEntrySchema = z
  .object({
    clientBoxId: z.string().min(1),
    clientBoxItemId: z.string().min(1),
    id: z.string().uuid(),
  })
  .strict();
export type DraftSaveBoxItemIdMapEntry = z.infer<typeof DraftSaveBoxItemIdMapEntrySchema>;

/**
 * @aiPurpose one entry in the regroup-batch body (#1178). The narrow
 *   `PATCH /api/requests/:id/boxes/:boxId/items/group-ordinals` endpoint
 *   accepts an array of these — the entire batch is one Prisma transaction.
 * @aiInvariant `itemId` must belong to `:boxId` (cross-box ids fail at
 *   batch validation, NOT silently rebound). `groupOrdinal` shares the
 *   bound with `RequestBoxItemSchema.groupOrdinal` so the regroup wire
 *   surface cannot widen the visual-grouping universe past
 *   `REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX`.
 */
export const BoxItemRegroupInputSchema = z
  .object({
    itemId: z.string().uuid(),
    groupOrdinal: z.number().int().min(0).max(REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX),
  })
  .strict();
export type BoxItemRegroupInput = z.infer<typeof BoxItemRegroupInputSchema>;

/**
 * @aiPurpose body of `PATCH /api/requests/:id/boxes/:boxId/items/group-ordinals`
 *   (#1178). Batch payload wrapping `BoxItemRegroupInputSchema`. Bound on
 *   array length mirrors `REQUEST_BOX_ITEMS_PER_BOX_MAX` so a single
 *   request body cannot grow past one full box's worth of nested rows
 *   (the endpoint is per-box anyway). Empty batch (`items: []`) is
 *   intentionally allowed at parse time and rejected later as a
 *   typed `batch.empty` validation error per D16 — keeps the
 *   parse-vs-domain validation boundary stable with the rest of the
 *   capsule.
 */
export const BoxItemsRegroupBodySchema = z
  .object({
    items: z.array(BoxItemRegroupInputSchema).max(REQUEST_BOX_ITEMS_PER_BOX_MAX),
  })
  .strict();
export type BoxItemsRegroupBody = z.infer<typeof BoxItemsRegroupBodySchema>;

/**
 * @aiPurpose response body of a successful regroup PATCH (#1178, D15).
 *   The new `updatedAt` lets the client rebaseline its `If-Match` token
 *   without a refetch round-trip; `changedCount` distinguishes the
 *   true-mutation path from D16 no-op batches (changedCount: 0).
 * @aiInvariant `updatedAt` is the strictly-newer parent-box value the
 *   server stamped inside the regroup transaction.
 */
export const BoxRegroupSuccessResponseSchema = z
  .object({
    boxId: z.string().uuid(),
    updatedAt: z.string().datetime(),
    changedCount: z.number().int().nonnegative(),
  })
  .strict();
export type BoxRegroupSuccessResponse = z.infer<typeof BoxRegroupSuccessResponseSchema>;

/**
 * @aiPurpose one entry in the structured 409 `box.regroup.override_conflict`
 *   payload (#1178, D13). One per `(boxId, groupOrdinal)` pair that the
 *   batch would empty while a `CarrierBidScope.BOX_GROUP` override in EITHER
 *   `CarrierBid` OR `RequestCarrierInsuranceQuote` still pins items to it.
 *   Frontend renders all entries in a single toast so the logist sees every
 *   conflict at once and asks the manager to clean overrides.
 * @aiSecurity carrier-side commercial fields (price, percentage, comment) are
 *   manager-only and MUST NOT appear in this payload. Only `carrierName` is
 *   exposed because the logist already sees the carrier list in the read-side
 *   bids/insurance tab; revealing names here adds no new information.
 */
export const BoxGroupOverrideConflictSchema = z
  .object({
    boxId: z.string().uuid(),
    groupOrdinal: z.number().int().min(0).max(REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX),
    domainKind: z.enum(['bid', 'insurance']),
    conflictingCarriers: z.array(
      z
        .object({
          carrierId: z.string().uuid(),
          carrierName: z.string().min(1),
        })
        .strict(),
    ),
  })
  .strict();
export type BoxGroupOverrideConflict = z.infer<typeof BoxGroupOverrideConflictSchema>;
